import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLive, useServices, ProblemError, LiveValue, constantLive, type Live, type RequestOptions } from '@/model';
import { i18next } from '@/i18n';
import { useTranslation } from 'react-i18next';
import { agentRuntime } from './data';
import { ConsentChanged } from '@/features/chatinfo/intent';
import en from './locales/en.json';
const keys = new Map(Object.entries(en).map(([key, value]) => [value as string, key]));
export function agentWord(source: string) { return String(i18next.t(Object.hasOwn(en, `web_${source}`) ? `web_${source}` : keys.get(source) ?? source, { ns: 'agents', defaultValue: source, keySeparator: false })); }
export function useWords() { const { t, i18n } = useTranslation('agents'); return { w: useCallback((source: string, values: Record<string, unknown> = {}) => String(t(Object.hasOwn(en, `web_${source}`) ? `web_${source}` : keys.get(source) ?? source, { defaultValue: source, keySeparator: false, ...values })), [t]), language: (i18n.resolvedLanguage?.startsWith('zh') ? 'zh-Hans' : 'en') as 'zh-Hans' | 'en' }; }
export function useRuntime() {
    const services = useServices();
    const client = useQueryClient();
    const runtime = agentRuntime(services);
    const scope = useLive(runtime.scope);
    useEffect(() => runtime.retain(client), [runtime, client]);
    return { runtime, services, scope, client };
}
export function useResource<T>(path: string, query: RequestOptions['query'] = {}, options: {
    enabled?: boolean;
    poll?: number | ((data: T | undefined) => number | false);
} = {}) {
    const { runtime, scope } = useRuntime();
    const result = useQuery<T, Error>({ queryKey: [...runtime.prefix, 'GET', path, query], enabled: scope.enabled && options.enabled !== false && !!path,
        queryFn: ({ signal }) => runtime.request<T>('GET', path, { query, signal, cause: 'open' }), retry: false, staleTime: 15000, gcTime: 0,
        refetchOnWindowFocus: true, refetchInterval: q => document.visibilityState === 'hidden' ? false : typeof options.poll === 'function' ? options.poll(q.state.data) : options.poll ?? false, refetchIntervalInBackground: false });
    const denied = result.error instanceof ProblemError && [401, 403, 404].includes(result.error.status);
    return { ...result, data: denied ? undefined : result.data, denied, waiting: !!scope.accountId && !scope.enabled, enabled: scope.enabled && options.enabled !== false };
}
/** Paged feature reads retain prior pages while the list stays virtualized. */
export function usePagedResource<T>(path: string, query: RequestOptions['query'], options: {
    enabled?: boolean;
    cursorKey: string;
    next(page: T): string | number | undefined;
}) {
    const { runtime, scope } = useRuntime();
    const result = useInfiniteQuery({ queryKey: [...runtime.prefix, 'pages', path, query], initialPageParam: undefined as string | number | undefined,
        queryFn: ({ signal, pageParam }) => runtime.request<T>('GET', path, { query: { ...query, [options.cursorKey]: pageParam }, signal, cause: pageParam === undefined ? 'open' : 'page' }),
        getNextPageParam: options.next, enabled: scope.enabled && options.enabled !== false, retry: false, staleTime: 15000, gcTime: 0 });
    const denied = result.error instanceof ProblemError && [401, 403, 404].includes(result.error.status);
    return { ...result, data: denied ? undefined : result.data?.pages, denied, waiting: !!scope.accountId && !scope.enabled, enabled: scope.enabled && options.enabled !== false };
}
export function useCommand() {
    const { runtime, scope } = useRuntime();
    const connection = useLive(runtime.services.sync.connection);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>();
    const pending = useRef(false);
    const mounted = useRef(true);
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
    useEffect(() => { pending.current = false; setBusy(false); setError(undefined); }, [scope.generation]);
    const run = useCallback(async <T,>(work: () => Promise<T>, done?: (result: T) => void) => {
        if (pending.current)
            return false;
        pending.current = true;
        setBusy(true);
        setError(undefined);
        const generation = runtime.scope.get().generation;
        try {
            const result = await work();
            if (!mounted.current || runtime.scope.get().generation !== generation)
                return false;
            runtime.refresh();
            done?.(result);
            return true;
        }
        catch (e) {
            if (mounted.current && runtime.scope.get().generation === generation)
                setError(e);
            return false;
        }
        finally {
            if (mounted.current && runtime.scope.get().generation === generation) {
                pending.current = false;
                setBusy(false);
            }
        }
    }, [runtime]);
    return { run, busy, blocked: !scope.enabled || connection.phase === 'offline', error, clear: () => setError(undefined) };
}
export function errorCopy(error: unknown): string {
    if (error instanceof ConsentChanged) return 'These settings changed. Review the current values and your choices before saving again.';
    if(error instanceof Error&&error.message==='trace_unavailable')return "This engine doesn't record turn prompts yet";
    if (error instanceof ProblemError) {
        if (error.code === 'builtin_agent') return 'The server refused this mount because built-in agents are shared by everyone. Duplicate the agent first to mount your projects.';
        if(error.code==='run_changed')return 'The current run changed. Try Last Turn again.';
        if(error.code==='claw_trace_unsupported')return "This engine doesn't record turn prompts yet";
        if (['proposal_revision_mismatch', 'proposal_resolved', 'item_revision_mismatch', 'item_policy_revision_mismatch', 'instructions_revision_mismatch', 'mutation_epoch_mismatch'].includes(error.code))
            return 'These settings changed. Review the current values and your choices before saving again.';
        if (error.status === 429)
            return 'Please wait before trying again.';
        if (error.status === 403 || error.status === 404)
            return 'This is no longer available.';
        if (error.code === 'capability_required')
            return 'This change cannot be fully reviewed in this version. You can dismiss it or open it in the App.';
        if (error.status === 400)
            return 'Check your choices and try again.';
    }
    return 'Couldn’t load. Check your connection and try again.';
}
const clocks = new Map<number, Live<number>>();
function visibleClock(interval: number): Live<number> {
    let clock = clocks.get(interval);
    if (clock)
        return clock;
    const value = new LiveValue(Date.now());
    let users = 0;
    let timer: ReturnType<typeof setInterval> | undefined;
    const tick = () => {
        if (document.visibilityState !== 'hidden')
            value.set(Date.now());
    };
    clock = { get: value.get, subscribe: listener => {
            users++;
            if (users === 1) {
                tick();
                timer = setInterval(tick, interval);
                document.addEventListener('visibilitychange', tick);
            }
            const stop = value.subscribe(listener);
            return () => {
                stop();
                users--;
                if (!users) {
                    clearInterval(timer);
                    document.removeEventListener('visibilitychange', tick);
                }
            };
        } };
    clocks.set(interval, clock);
    return clock;
}
/** One visible clock per interval, shared by all active rows/cards. */
export function useClock(active = true, interval = 1000) { return useLive(useMemo(() => active ? visibleClock(interval) : constantLive(Date.now()), [active, interval])); }
